import type { printOnDemand } from "common/utils/models";
/**
 * Application-wide constants for the design lab
 */

// Product selection URLs
export const PRODUCT_LIST_PATH = "/?customizable=Customizable&bare=true";

// Pricing constants
export const PRODUCT_ID = "432292";
export const STANDARD_DTF_CATALOG_NUMBER = "DTF001";
export const DEFAULT_FREE_SHIPPING_THRESHOLD = 59;
// Size constants
export const SIZES = {
  EXTRA_SMALL: "XS",
  SMALL: "S",
  MEDIUM: "M",
  LARGE: "L",
  EXTRA_LARGE: "XL",
  DOUBLE_XL: "2XL",
  TRIPLE_XL: "3XL",
  QUAD_XL: "4XL",
  PENTA_XL: "5XL"
} as const;

export const DEFAULT_SAMPLE_SIZE = SIZES.LARGE;

export const BLANKS_SIZES: printOnDemand.getProduct.Size[] = [
  SIZES.EXTRA_SMALL,
  SIZES.SMALL,
  SIZES.MEDIUM,
  SIZES.LARGE,
  SIZES.EXTRA_LARGE,
  SIZES.DOUBLE_XL,
  SIZES.TRIPLE_XL,
  SIZES.QUAD_XL,
  SIZES.PENTA_XL
];

export type SizeValue = (typeof SIZES)[keyof typeof SIZES];

export const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keypress",
  "scroll",
  "touchstart",
  "click",
  "wheel"
];

// Background types
export const BACKGROUND_TYPE = {
  SHIRT: "shirt",
  GREEN: "green",
  CHECKERED: "checkered",
  GRAY: "gray"
} as const;

export type BackgroundType = (typeof BACKGROUND_TYPE)[keyof typeof BACKGROUND_TYPE];

// Background colors
export const COLORS = {
  GREEN_BACKGROUND: "#04F404",
  GREEN_SCREEN: "#00FF00",
  GRAY_BACKGROUND: "#E5E7EB",
  WHITE_BACKGROUND: "#FFFFFF",
  BLACK_TEXT: "#000000"
} as const;

// Background patterns
export const PATTERNS = {
  CHECKERED_SVG:
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQiIGhlaWdodD0iNCIgZmlsbD0iI0M1QzVDNSIvPgo8cmVjdCB5PSI4IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjQzVDNUM1Ii8+CjxyZWN0IHg9IjE2IiB5PSI4IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiB0cmFuc2Zvcm09InJvdGF0ZSgtMTgwIDE2IDgpIiBmaWxsPSIjQzVDNUM1Ii8+CjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjQiIGhlaWdodD0iNCIgdHJhbnNmb3JtPSJyb3RhdGUoLTE4MCAxNiAxNikiIGZpbGw9IiNDNUM1QzUiLz4KPHJlY3QgeD0iOCIgd2lkdGg9IjQiIGhlaWdodD0iNCIgZmlsbD0iI0M1QzVDNSIvPgo8cmVjdCB4PSI4IiB5PSI4IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjQzVDNUM1Ii8+CjxyZWN0IHg9IjgiIHk9IjgiIHdpZHRoPSI0IiBoZWlnaHQ9IjQiIHRyYW5zZm9ybT0icm90YXRlKC0xODAgOCA4KSIgZmlsbD0iI0M1QzVDNSIvPgo8cmVjdCB4PSI4IiB5PSIxNiIgd2lkdGg9IjQiIGhlaWdodD0iNCIgdHJhbnNmb3JtPSJyb3RhdGUoLTE4MCA4IDE2KSIgZmlsbD0iI0M1QzVDNSIvPgo8cmVjdCB4PSI0IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjOUI5QjlCIi8+CjxyZWN0IHg9IjQiIHk9IjgiIHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiM5QjlCOUIiLz4KPHJlY3QgeD0iMTIiIHk9IjgiIHdpZHRoPSI0IiBoZWlnaHQ9IjQiIHRyYW5zZm9ybT0icm90YXRlKC0xODAgMTIgOCkiIGZpbGw9IiM5QjlCOUIiLz4KPHJlY3QgeD0iMTIiIHk9IjE2IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiB0cmFuc2Zvcm09InJvdGF0ZSgtMTgwIDEyIDE2KSIgZmlsbD0iIzlCOUI5QiIvPgo8cmVjdCB4PSIxMiIgd2lkdGg9IjQiIGhlaWdodD0iNCIgZmlsbD0iIzlCOUI5QiIvPgo8cmVjdCB4PSIxMiIgeT0iOCIgd2lkdGg9IjQiIGhlaWdodD0iNCIgZmlsbD0iIzlCOUI5QiIvPgo8cmVjdCB4PSI0IiB5PSI4IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiB0cmFuc2Zvcm09InJvdGF0ZSgtMTgwIDQgOCkiIGZpbGw9IiM5QjlCOUIiLz4KPHJlY3QgeD0iNCIgeT0iMTYiIHdpZHRoPSI0IiBoZWlnaHQ9IjQiIHRyYW5zZm9ybT0icm90YXRlKC0xODAgNCAxNikiIGZpbGw9IiM5QjlCOUIiLz4KPC9zdmc+Cg=="
} as const;
